import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';
import { NSchema as n } from '@nostrify/nostrify';
import { AppShell } from '@/components/AppShell';
import { NoteCard } from '@/components/note';
import { UserAvatar, pubkeyToNpub } from '@/components/user';
import { useAuthor } from '@/hooks/useAuthor';
import { useProfileNotes } from '@/hooks/useFeeds';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { shortId } from '@/lib/jumble';

function decodePubkey(identifier: string): string | undefined {
  try {
    const d = nip19.decode(identifier);
    if (d.type === 'npub') return d.data;
    if (d.type === 'nprofile') return d.data.pubkey;
    return undefined;
  } catch {
    return /^[0-9a-f]{64}$/i.test(identifier) ? identifier.toLowerCase() : undefined;
  }
}

export function ProfilePage({ identifier }: { identifier: string }) {
  const pubkey = decodePubkey(identifier);
  const author = useAuthor(pubkey);
  const notes = useProfileNotes(pubkey);
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutate: publish } = useNostrPublish();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('notes');

  const metadata: NostrMetadata | undefined = useMemo(() => {
    if (author.data?.metadata) return author.data.metadata;
    if (author.data?.event) {
      try {
        return n.json().pipe(n.metadata()).parse(author.data.event.content);
      } catch { return undefined; }
    }
    return undefined;
  }, [author.data]);

  // follow state
  const followList = useQuery({
    queryKey: ['jumble', 'my-follows', user?.pubkey],
    enabled: !!user,
    queryFn: async ({ signal }) => {
      const [list] = await nostr.query([{ kinds: [3], authors: [user!.pubkey], limit: 1 }], { signal: signal ?? AbortSignal.timeout(3000) });
      return (list?.tags ?? []).filter((t) => t[0] === 'p').map((t) => t[1]);
    },
  });
  const isFollowing = !!pubkey && (followList.data ?? []).includes(pubkey);

  const toggleFollow = () => {
    if (!user || !pubkey) return;
    const current = (followList.data ?? []).filter((p) => p !== pubkey);
    const tags = [...current.map((p) => ['p', p]), ...(isFollowing ? [] : [['p', pubkey]])];
    publish({ kind: 3, content: '', tags }, { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jumble', 'my-follows'] }) });
  };

  useSeoMeta({
    title: metadata ? `${metadata.name ?? shortId(pubkey ?? '')} — Jumble` : 'Profile — Jumble',
    description: metadata?.about?.slice(0, 160) ?? 'Nostr profile.',
  });

  if (!pubkey) {
    return <AppShell title="Profile"><p className="p-8 text-center text-muted-foreground">Invalid profile identifier.</p></AppShell>;
  }

  const banner = metadata?.banner ? sanitizeUrl(metadata.banner) : undefined;
  const npub = pubkeyToNpub(pubkey);
  const allNotes = (notes.data?.pages ?? []).flat();
  const mediaNotes = allNotes.filter((e) => /https?:\/\/\S+\.(jpg|jpeg|png|gif|webp|mp4|webm)/i.test(e.content));
  const replyNotes = allNotes.filter((e) => e.tags.some((t) => t[0] === 'e'));
  const shown = tab === 'media' ? mediaNotes : tab === 'replies' ? replyNotes : allNotes;

  return (
    <AppShell title={metadata?.display_name ?? metadata?.name ?? shortId(pubkey)}>
      {banner && <div className="h-36 w-full overflow-hidden bg-muted sm:h-48"><img src={banner} alt="" className="h-full w-full object-cover" /></div>}
      <div className="border-b p-4">
        <div className="flex items-start justify-between">
          <div className="-mt-10 sm:-mt-12">
            <div className="rounded-full border-4 border-background">
              <UserAvatar pubkey={pubkey} size="xl" />
            </div>
          </div>
          {user && user.pubkey !== pubkey && (
            <Button size="sm" variant={isFollowing ? 'outline' : 'default'} className="rounded-full" onClick={toggleFollow}>
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
          )}
        </div>
        <h2 className="mt-2 text-xl font-extrabold">{metadata?.display_name ?? metadata?.name ?? shortId(pubkey)}</h2>
        {metadata?.nip05 && <p className="text-sm text-muted-foreground">{metadata.nip05}</p>}
        {metadata?.about && <p className="mt-2 text-[15px] whitespace-pre-wrap break-words">{metadata.about}</p>}
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {metadata?.website && <a href={sanitizeUrl(metadata.website) ?? '#'} target="_blank" rel="noreferrer" className="text-sky-500 hover:underline">{metadata.website.replace(/^https?:\/\//, '')}</a>}
          <span className="font-mono">{shortId(npub, 12)}</span>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start rounded-none border-b bg-transparent">
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="replies">Replies</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-0">
          {notes.isLoading ? (
            <div className="space-y-2 p-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
          ) : shown.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nothing here yet.</p>
          ) : (
            <>
              {shown.map((e: NostrEvent) => <NoteCard key={e.id} event={e} />)}
              {notes.hasNextPage && (
                <div className="flex justify-center py-4"><Button variant="outline" size="sm" onClick={() => notes.fetchNextPage()}>Load more</Button></div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
      <div className="pb-20 md:pb-4" />
    </AppShell>
  );
}

export function MePage() {
  const { user } = useCurrentUser();
  if (!user) {
    return <AppShell title="Me"><p className="p-8 text-center text-muted-foreground">Log in to see your profile.</p></AppShell>;
  }
  return <ProfilePage identifier={pubkeyToNpub(user.pubkey)} />;
}
